import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const InspectorModalContext = createContext(null);

const HISTORY_STORAGE_KEY = 'inspector_history';
const MAX_HISTORY = 50;

export function InspectorModalProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  const [history, setHistory] = useState(() => {
    // Load history from sessionStorage
    try {
      const stored = sessionStorage.getItem(HISTORY_STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Failed to load inspector history:', error);
      return [];
    }
  });
  const [historyIndex, setHistoryIndex] = useState(-1);

  // Save history to sessionStorage whenever it changes
  useEffect(() => {
    try {
      sessionStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(history));
    } catch (error) {
      console.error('Failed to save inspector history:', error);
    }
  }, [history]);

  const openInspector = useCallback((nodeId, nodeType) => {
    setIsOpen(true);
    navigateToNode(nodeId, nodeType);
  }, []);

  const closeInspector = useCallback(() => {
    setIsOpen(false);
  }, []);

  const toggleInspector = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const navigateToNode = useCallback((nodeId, nodeType) => {
    const newEntry = {
      nodeId,
      nodeType,
      timestamp: Date.now()
    };

    setHistory(prev => {
      // If we're not at the end of history, truncate forward history
      const truncated = historyIndex === -1 ? prev : prev.slice(0, historyIndex + 1);

      // Add new entry and limit to MAX_HISTORY
      const updated = [...truncated, newEntry].slice(-MAX_HISTORY);
      return updated;
    });

    setHistoryIndex(prev => {
      const truncated = prev === -1 ? history.length : prev + 1;
      return Math.min(truncated, MAX_HISTORY - 1);
    });
  }, [historyIndex, history.length]);

  const goBack = useCallback(() => {
    if (historyIndex > 0) {
      setHistoryIndex(prev => prev - 1);
    }
  }, [historyIndex]);

  const goForward = useCallback(() => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(prev => prev + 1);
    }
  }, [historyIndex, history.length]);

  const canGoBack = historyIndex > 0;
  const canGoForward = historyIndex < history.length - 1;
  const currentNode = historyIndex >= 0 ? history[historyIndex] : null;

  const value = {
    isOpen,
    openInspector,
    closeInspector,
    toggleInspector,
    navigateToNode,
    goBack,
    goForward,
    canGoBack,
    canGoForward,
    currentNode,
    history,
    historyIndex
  };

  return (
    <InspectorModalContext.Provider value={value}>
      {children}
    </InspectorModalContext.Provider>
  );
}

export function useInspector() {
  const context = useContext(InspectorModalContext);
  if (!context) {
    throw new Error('useInspector must be used within InspectorModalProvider');
  }
  return context;
}
